import { UseFormReturn } from 'react-hook-form';
import { formulaTraceAttrs } from '@/context/DocumentFormulaTraceContext';
import { IDocumentEditInModal } from '@models/Forms/IForms';
import './EditInModal.scss';
import { IField, IFieldElem } from '@models/IFormData';
import { useState } from 'react';
import SubForm from './SubForm';
import { IDocumentData } from '@models/document/IDocumentData';
import { MdEdit } from 'react-icons/md';
import DOMPurify from 'dompurify';
import { useFormulaDisplay, useActivated, useFormulaState } from '@molecules/formbuilder/hooks';
import { EnsureExternalDataSourceLoaded } from '@/utils/externalDataSourceUtils';

const postProcessEditInModalDisplay = (v: string) => v.replace(/\'/g, '').replace(/\\n/g, '\n');

export interface IEditInModalProps {
    editInModal?: IDocumentEditInModal;
    formMethods: UseFormReturn<any>;
    isEdit: boolean;
    isNew: boolean;
    fields: Record<string, IFieldElem>;
    docId?: string;
    ensureExternalDataSourceLoaded?: EnsureExternalDataSourceLoaded;
}

const EditInModal = ({
    editInModal,
    formMethods,
    isEdit,
    isNew,
    fields,
    docId,
    ensureExternalDataSourceLoaded,
}: IEditInModalProps) => {
    const { activated } = useActivated();
    const visibility = useFormulaState(editInModal?.visibilityRules, fields, formMethods, isEdit, isNew, activated, {
        fallback: true,
        ensureExternalDataSourceLoaded,
        trace: editInModal?.id
            ? {
                  targetId: editInModal.id,
                  targetKind: 'field',
                  targetName: editInModal.modalCaption || editInModal.text || editInModal.id,
                  ruleKind: 'visibility',
              }
            : undefined,
    });
    const display = useFormulaDisplay(editInModal?.text, fields, formMethods, isEdit, isNew, activated, {
        fallback: editInModal?.text,
        postProcess: postProcessEditInModalDisplay,
        ensureExternalDataSourceLoaded,
        trace: editInModal?.id
            ? {
                  targetId: editInModal.id,
                  targetKind: 'field',
                  targetName: editInModal.modalCaption || editInModal.text || editInModal.id,
                  ruleKind: 'formula',
              }
            : undefined,
    });

    const [showModal, setShowModal] = useState<boolean>(false);

    const onClick = (e: any) => {
        e.preventDefault();
        e.stopPropagation();

        setShowModal(!showModal);
    };

    const onSubmitHandler = async (data: IField[]) => {
        data.forEach((field) => {
            const fieldProp = fields[field.name];

            formMethods.setValue(fieldProp.name as any, field.value as any);
        });

        if (activated.current) {
            setShowModal(!showModal);
        }
    };

    return editInModal && (isEdit || isNew) && visibility ? (
        <div
            className="editInModal-box"
            data-testid={editInModal.id ? `edit-in-modal-${editInModal.id}` : undefined}
            {...formulaTraceAttrs(editInModal.id, 'field')}
        >
            {editInModal.textPosition === 'left' && (
                <div
                    className="content-text"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(display.value, { ADD_ATTR: ['target'] }) }}
                />
            )}
            <div
                className="editInModal-btn"
                onClick={onClick}
                style={editInModal.color ? { color: editInModal.color } : {}}
            >
                <MdEdit size="18px" />
            </div>
            {editInModal.textPosition === 'right' && (
                <div
                    className="content-text"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(display.value, { ADD_ATTR: ['target'] }) }}
                />
            )}
            {showModal && (
                <SubForm
                    form={editInModal.rows}
                    docId={docId}
                    isEdit={true}
                    formFields={formMethods.getValues() as IDocumentData}
                    variables={fields.variables}
                    onClose={() => {
                        setShowModal(!showModal);
                    }}
                    onSubmit={onSubmitHandler}
                    submitButtonCaption={editInModal.submitButtonCaption}
                    modalCaption={editInModal.modalCaption}
                ></SubForm>
            )}
        </div>
    ) : (
        <></>
    );
};

export default EditInModal;
